import {RefObject} from "react"
import {WellnessMedia, WellnessMediaType} from "@irati/types"
import tracking from "@irati-tracking/wellness"
import moment from "moment-timezone"

const zoneName = moment.tz.guess()
const now = Date.now()

const endDateHour = (liveVideoEndTime: string) => moment.tz(liveVideoEndTime, zoneName).format("HH:mm")
const startDate = (liveVideoStartTime: string, displayShortMonth = false) => {
  const monthFormat = displayShortMonth ? "MMM" : "MMMM"
  return moment.tz(liveVideoStartTime, zoneName).format(`Do ${monthFormat} @ HH:mm`)
}

export const upcomingEventDateAndTime = (liveVideoStartTime: string, liveVideoEndTime: string) => {
  return (
    /* eslint-disable-next-line max-len */
    `${moment.tz(liveVideoStartTime, zoneName).format("DD MMMM @ HH:mm - ")}${endDateHour(liveVideoEndTime)} ${moment.tz(zoneName).format("z")}`
  )
}

export const videoInProgress = (liveVideoStartTime: string, liveVideoEndTime: string) => {
  return moment.tz(liveVideoStartTime, zoneName).unix() < moment(now).unix() &&
    moment.tz(liveVideoEndTime, zoneName).unix() > moment(now).unix()
}

export const liveVideoDate = (
  liveVideoStartTime: string,
  liveVideoEndTime: string,
  options: {displayShortMonth: boolean} = {displayShortMonth: false}) =>
  `${startDate(liveVideoStartTime, options.displayShortMonth)} - ${endDateHour(liveVideoEndTime)} ${moment.tz(zoneName).format("z")}`

export const liveVideoMonth = (liveVideoStartTime: string) => {
  return `${moment.tz(liveVideoStartTime, zoneName).format("MMM")}`
}

export const liveVideoDayNumber = (liveVideoStartTime: string) => {
  return `${moment.tz(liveVideoStartTime, zoneName).format("DD")}`
}

export const liveVideoDayText = (liveVideoStartTime: string) => {
  return `${moment.tz(liveVideoStartTime, zoneName).format("dddd")}`
}

export const liveVideoHours = (liveVideoStartTime: string, liveVideoEndTime: string) => {
  /* eslint-disable-next-line max-len */
  return `${moment.tz(liveVideoStartTime, zoneName).format("HH:mm - ")}${moment.tz(liveVideoEndTime, zoneName).format("HH:mm")} ${moment.tz(zoneName).format("z")}`
}

export const startedStreaming = (liveVideoStartTime: string) => {
  return `${moment(liveVideoStartTime).fromNow()}`
}

export const SEE_ALL = "See all"

export const formatFromTimeToHoursAndMinutes = (duration: string): string => {
  // We are expecting a mm:ss format
  const durationArray = duration.split(":")
  let minutesInt = parseInt(durationArray[0], 10)
  const secondsInt = parseInt(durationArray[1], 10)
  if (secondsInt >= 30) minutesInt++

  const hours = Math.floor(minutesInt / 60)
  const formattedHours = hours ? `${hours} h` : ""
  const minutes = minutesInt - hours * 60
  let formattedMinutes = ""
  if (minutes) {
    formattedMinutes = minutes === 1 ? `${minutes} min` : `${minutes} mins`
  }
  const space = hours && minutes ? " " : ""
  return `${formattedHours}${space}${formattedMinutes}`
}

export const getImageUrlByWidth = (url: string, width: string | number) => {
  const urlArray = url.split("/")
  const uploadIndex = urlArray.findIndex((e) => e === "upload")
  const injectDimensionStr = `w_${width},c_scale`
  urlArray.splice(uploadIndex + 1, 0, injectDimensionStr)
  return urlArray.join("/")
}

export const getHeroCTACopy = (type?: WellnessMediaType) => {
  // TODO switch for translations when we'll start translating content
  switch (type) {
    case WellnessMediaType.SingleVideo:
      return "Watch now"
    case WellnessMediaType.SingleAudio:
      return "Listen now"
    case WellnessMediaType.VideoCourse:
    case WellnessMediaType.AudioCourse:
      return "Go to course"
    default:
      return "See more"
  }
}

export const getMediaTypeCopy = (type?: WellnessMediaType) => {
  // TODO switch for translations when we'll start translating content
  switch (type) {
    case WellnessMediaType.SingleVideo:
      return "Video"
    case WellnessMediaType.SingleAudio:
      return "Audio"
    case WellnessMediaType.VideoCourse:
    case WellnessMediaType.AudioCourse:
      return "Course"
    case WellnessMediaType.LiveVideo:
      return "Live"
    default:
      return "Media"
  }
}

export const onProgress = (
  currentSeconds: number,
  totalSeconds: number,
  media: WellnessMedia,
  isQuarterTracked: RefObject<boolean>,
  isMidTracked: RefObject<boolean>,
  isThreeQuartersTracked: RefObject<boolean>,
  isFinishTracked: RefObject<boolean>
) => {
  const quarterThreshold = Math.trunc((totalSeconds * 25) / 100)
  const midThreshold = Math.trunc((totalSeconds * 50) / 100)
  const threeQuartersThreshold = Math.trunc((totalSeconds * 75) / 100)
  const finishThreshold = Math.trunc((totalSeconds * 90) / 100)

  if (currentSeconds >= quarterThreshold && !isQuarterTracked?.current) {
    isQuarterTracked.current = true
    tracking.progressMedia(media, 25)
  }
  if (currentSeconds >= midThreshold && !isMidTracked.current) {
    isMidTracked.current = true
    tracking.progressMedia(media, 50)
  }
  if (currentSeconds >=  threeQuartersThreshold && !isThreeQuartersTracked.current) {
    isThreeQuartersTracked.current = true
    tracking.progressMedia(media, 75)
  }
  // We consider the playback as "finished" at 90% ot the total duration
  if (currentSeconds >= finishThreshold && !isFinishTracked.current) {
    isFinishTracked.current = true
    tracking.stopMedia(media)
  }
}

export const getFormattedRentalPeriodUnit = (rentalPeriod: number, rentalPeriodUnit: string) => {
  if(rentalPeriod === 1 && rentalPeriodUnit?.charAt(rentalPeriodUnit.length - 1) === "S") {
    return rentalPeriodUnit?.slice(0, -1).toLowerCase()
  } else {
    return rentalPeriodUnit?.toLowerCase()
  }
}
